import React from 'react';
import { Alert, Box, CircularProgress, Button } from '@mui/material';
import { useLoyaltyConfig } from '../hooks/useLoyaltyConfig';
import { useUserProgress } from '../hooks/useUserProgress';
import { useNavigate } from 'react-router-dom';

interface FeatureGuardProps {
  featureId: string;
  children: React.ReactNode;
}

const FeatureGuard: React.FC<FeatureGuardProps> = ({ featureId, children }) => {
  const { config, loading: configLoading, error: configError } = useLoyaltyConfig();
  const { progress, loading: progressLoading, error: progressError } = useUserProgress();
  const navigate = useNavigate();

  if (configLoading || progressLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (configError || progressError || !config) {
    return <Alert severity="error">Unable to verify access</Alert>;
  }

  const feature = config.features?.find((f: any) => f.id === featureId);
  if (!feature) {
    return <Alert severity="warning">Feature not configured</Alert>;
  }

  // If feature is globally disabled, allow everyone to see full content
  if (feature.enabled === false) {
    return <>{children}</>;
  }

  const userLevel = progress?.currentXmLevel || 'none';
  const allowed = feature.levels.includes(userLevel);
  const requiredLevels = feature.levels.join(', ');

  // Determine next required level based on user level order
  const levelOrder = ['none','bronze','silver','gold'];
  const userIndex = levelOrder.indexOf(userLevel);
  const sortedRequired = levelOrder.filter(l => feature.levels.includes(l));
  const nextLevel = sortedRequired.find(l => levelOrder.indexOf(l) > userIndex) || sortedRequired[0];

  // Calculate points needed if nextLevel is silver or gold
  let pointsNeeded: number | null = null;
  if (nextLevel === 'silver' || nextLevel === 'gold') {
    const levelCfg = config.levels?.find((lvl: any)=> lvl.id === nextLevel);
    const target = levelCfg?.monthlyPointsRequired ?? 0;
    const currentPts = progress?.monthlyPoints || 0;
    pointsNeeded = Math.max(0, target - currentPts);
  }

  return (
    <>
      {!allowed && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 2, 
            mx: 2, 
            mt: { xs: 12, md: 14 },
            position: 'sticky',
            top: { xs: 64, md: 80 },
            zIndex: 1000,
            boxShadow: 2
          }}
          action={
            nextLevel === 'bronze' ? (
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => navigate('/profile')}
                sx={{ fontWeight: 'bold' }}
              >
                Complete Profile
              </Button>
            ) : null
          }
        >
          {nextLevel === 'bronze' ? (
            <>🔒 This feature requires <strong>Bronze</strong> level. Complete your trading experience and preferred pairs in your profile to unlock it.</>
          ) : (
            <>🔒 This feature requires <strong>{nextLevel.charAt(0).toUpperCase()+nextLevel.slice(1)}</strong> level. {pointsNeeded !== null && pointsNeeded>0 && userIndex >= 1 ? (<span> Earn <strong>{pointsNeeded}</strong> more points this month to unlock it.</span>): null}</>
          )}
        </Alert>
      )}
      <Box sx={{ 
        opacity: allowed ? 1 : 0.6, 
        pointerEvents: allowed ? 'auto' : 'none',
        filter: allowed ? 'none' : 'grayscale(20%)'
      }}>
        {children}
      </Box>
    </>
  );
};

export default FeatureGuard; 